import { useEffect, useState } from "react";
import { Sparkles, X, Telescope, Moon } from "lucide-react";
import axios from "axios";
import useSubscription from "@/hooks/useSubscription";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY_PREFIX = "ep_day_of_event_dismissed:";

/**
 * DayOfEventBanner — soft banner shown to ACTIVE subscribers on the day
 * of a synced sky event, with a plain-English viewing tip.
 *
 * Behaviour:
 *   • Only rendered when `useSubscription().active === true`
 *   • Fetches /api/sky-events/today (public endpoint) once on mount
 *   • Displays the very next event happening within 24 hours + a viewing tip
 *   • Each event's dismissal is remembered in localStorage by ISO start,
 *     so dismissing "Perseids peak tonight" won't hide the next event
 */
export default function DayOfEventBanner() {
  const { active, loading: subLoading } = useSubscription();
  const [event, setEvent] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (subLoading || !active) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/sky-events/today`);
        const first = data?.events?.[0];
        if (!first || cancelled) return;
        // Skip if this specific event was already dismissed today
        try {
          const key = DISMISS_KEY_PREFIX + first.start;
          if (localStorage.getItem(key)) {
            setDismissed(true);
            return;
          }
        } catch { /* localStorage disabled */ }
        setEvent(first);
      } catch { /* fail quiet — banner is best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [active, subLoading]);

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY_PREFIX + event.start, "1"); } catch { /* */ }
    setDismissed(true);
  };

  if (!active || subLoading || !event || dismissed) return null;

  const iconMap = {
    meteor_shower: Sparkles,
    moon_phase:    Moon,
    comet:         Telescope,
    eclipse:       Telescope,
    solar:         Sparkles,
  };
  const Icon = iconMap[event.kind] || Telescope;

  const whenLabel = event.hours_from_now < 1
    ? "happening now"
    : event.hours_from_now < 4
      ? `in ${Math.round(event.hours_from_now)} hours`
      : "tonight";

  return (
    <div
      className="mb-8 stagger-in flex items-start gap-4 md:gap-5 px-5 md:px-6 py-4 md:py-5 rounded-2xl border border-[color:var(--gold)]/25 bg-[color:var(--gold)]/[0.05]"
      style={{ boxShadow: "0 12px 40px -18px rgba(212, 162, 76, 0.2)" }}
      data-testid="day-of-event-banner"
    >
      <div className="shrink-0 w-10 h-10 rounded-full border border-[color:var(--gold)]/40 flex items-center justify-center bg-[color:var(--bg-base)]">
        <Icon size={16} className="text-[color:var(--gold)]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="label-overline text-[10px] text-[color:var(--gold)] flex items-center gap-2">
          today · {whenLabel}
        </div>
        <div
          className="mt-1 font-serif-display text-[color:var(--parchment)] text-lg md:text-xl tracking-tight"
          data-testid="day-of-event-title"
        >
          {event.title}
        </div>
        <p
          className="mt-2 font-serif-italic text-[color:var(--parchment-soft)] text-sm md:text-[15px] leading-relaxed"
          data-testid="day-of-event-tip"
        >
          {event.viewing_tip}
        </p>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[color:var(--text-muted)] hover:text-[color:var(--parchment)] hover:bg-[color:var(--gold)]/10 transition"
        aria-label="Dismiss"
        data-testid="day-of-event-dismiss"
      >
        <X size={13} />
      </button>
    </div>
  );
}
